import React from 'react';
import { MuiModal } from 'ui/MuiModal';
import { Button, FormControl, FormHelperText, Grid, MenuItem, Select, Typography } from '@mui/material';
import { titleize } from 'lib/helpers';
import { Row } from 'ui/Flex';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { EPremiumLoyaltyState } from '../model';

interface TieredLoyaltyStateModalProps {
    onClose: () => void;
    open: boolean;
    tierState: EPremiumLoyaltyState;
    readyToActivate?: boolean;
    onSubmit: (state: string) => void;
    loading?: boolean;
}

export const TieredLoyaltyStateModal: React.FC<TieredLoyaltyStateModalProps> = ({
    onClose,
    open,
    tierState,
    readyToActivate,
    loading,
    onSubmit
}) => {
    const [state, setState] = React.useState(tierState ?? '');
    const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setState(e.target.value);
    }, []);
    const handleClick = React.useCallback(() => {
        onSubmit(state);
    }, [onSubmit, state]);
    React.useEffect(() => {
        setState(tierState);
    }, [tierState]);
    const isError = React.useMemo(
        () => !readyToActivate && state !== EPremiumLoyaltyState.INACTIVE,
        [readyToActivate, state]
    );
    const disabled = React.useMemo(
        () => state === tierState || isError || state === '' || loading,
        [isError, loading, state, tierState]
    );
    return (
        <MuiModal open={open} onClose={onClose}>
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Typography variant="h6" color="primary">
                        Change your Tier scheme state
                    </Typography>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth error={isError} margin="none">
                        <Select variant="standard" value={state ?? ''} fullWidth onChange={handleChange}>
                            <MenuItem value={EPremiumLoyaltyState.INACTIVE}>
                                {titleize(EPremiumLoyaltyState.INACTIVE)}
                            </MenuItem>
                            <MenuItem value={EPremiumLoyaltyState.REALTIME}>
                                {titleize(EPremiumLoyaltyState.REALTIME)}
                            </MenuItem>
                            <MenuItem value={EPremiumLoyaltyState.SCHEDULED}>
                                {titleize(EPremiumLoyaltyState.SCHEDULED)}
                            </MenuItem>
                            <MenuItem value={EPremiumLoyaltyState.SCHEDULED_AND_REALTIME}>
                                {titleize(EPremiumLoyaltyState.SCHEDULED_AND_REALTIME)}
                            </MenuItem>
                        </Select>
                        {isError && (
                            <FormHelperText sx={{ mx: 0 }} error>
                                At least one metric and listener is required to enable the tiered loyalty.
                            </FormHelperText>
                        )}
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <Row gutter flex={1} align="flex-end">
                        <Button variant="outlined" onClick={onClose}>
                            Close
                        </Button>
                        <LoadingButton
                            variant="contained"
                            onClick={handleClick}
                            disabled={disabled}
                            loading={loading}
                        >
                            Submit
                        </LoadingButton>
                    </Row>
                </Grid>
            </Grid>
        </MuiModal>
    );
};
